import { API_URL } from "../config";
import { resolveLaunchContext } from "../telegram/launch";

/**
 * Fire-and-forget client-fault beacon → api `POST /client-error` → `ErrorLog` → the error group.
 *
 * WHY THIS EXISTS: on 2026-07-31 a player hit a dead-end «اتصال برقرار نشد» modal twice, and we
 * had NOTHING server-side to look at — the reconnect cycle logs to the phone's console, and a
 * bot takeover writes no log line at all. The diagnosis had to be reconstructed from a photo of
 * the screen, and two successive reconstructions were wrong. Anything that puts a modal in front
 * of a player now says so out loud, on the server, with the state that explains it.
 *
 * Rules, so this can be left on permanently:
 *  - NEVER throws and never rejects — a reporting failure must not become the fault it reports;
 *  - `keepalive` so a report survives the page being backgrounded or torn down mid-send, which
 *    is exactly the moment we most want to hear from;
 *  - the server fingerprint-dedupes, so a fault across many phones is one row with a count.
 */
export function reportClientFault(context: string, detail: Record<string, unknown>): void {
  try {
    const init = resolveLaunchContext().initDataRaw;
    if (!init) return; // outside Telegram (dev/preview) — nothing to authenticate with
    void fetch(`${API_URL}/client-error`, {
      method: "POST",
      keepalive: true,
      headers: { "content-type": "application/json", authorization: `tma ${init}` },
      body: JSON.stringify({ context, detail: summarize({ ...detail, bundle: bundleId() }) }),
    }).catch(() => undefined);
  } catch {
    // never let telemetry break the app
  }
}

/**
 * The content hash of the JS bundle this client is actually running (`BqubOkdZ` from
 * `/assets/index-BqubOkdZ.js`), on EVERY beacon.
 *
 * WHY: a Telegram Mini App keeps its loaded document indefinitely while minimised, so at any
 * moment the live population is spread across several deploys — three landed on 2026-08-01 alone.
 * «Which bundle was this?» is the first question of every client-side investigation and it was
 * previously unanswerable; a half-rendered table on 2026-08-01 could not be attributed to a build
 * for exactly this reason.
 *
 * Read from the DOM rather than injected at build time: the module script tag carries the hash
 * Vite already generated, so there is nothing to keep in sync and no build config to forget.
 */
let cachedBundle: string | null = null;
function bundleId(): string {
  if (cachedBundle !== null) return cachedBundle;
  let id = "unknown";
  try {
    const src = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src ?? "";
    id = /index-([A-Za-z0-9_-]+)\.js/.exec(src)?.[1] ?? "unknown";
  } catch {
    // a beacon must never be the thing that throws
  }
  cachedBundle = id;
  return id;
}

/** Test seam — the module caches the hash after the first read. */
export function __resetBundleIdForTests(): void {
  cachedBundle = null;
}

/** Flatten to one readable line; the sink normalizes numbers when fingerprinting. */
function summarize(detail: Record<string, unknown>): string {
  return Object.entries(detail)
    .map(([k, v]) => `${k}=${render(v)}`)
    .join(" ");
}

function render(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return String(value);
  try {
    return JSON.stringify(value) ?? "?";
  } catch {
    return "?";
  }
}
